import { ScriptType } from "@/enums/campaigns";
import { apiFetch, apiLive } from "@/lib/api";
import type { PhoneScript } from "@/types/campaigns";
import type { UseCase } from "@/types/scripts";
import { GAIL_API } from "@/utils/common/constants/routes";
import { sanitizePhoneScriptForSave } from "@/utils/inbound/scriptSave";
import { getScriptApiUrl, type ScriptApiVersion } from "@/utils/scriptVersioning/api";
import {
  hydrateUseCasesFromScript,
  shouldSkipUseCaseBatchFetch,
} from "@/utils/scripts/embeddedUseCases";

/**
 * Copy preview use cases onto the script when the document itself has none.
 * v2 PUTs need owned `useCases[]`; onboarding preview keeps that list in
 * component state even when the GET payload was association-only.
 */
export function withPreviewUseCases(
  script: PhoneScript,
  useCases: UseCase[]
): PhoneScript {
  if ((script.useCases?.length ?? 0) > 0 || useCases.length === 0) {
    return script;
  }

  return {
    ...script,
    useCases,
  };
}

export async function putOnboardingPhoneScript(
  script: PhoneScript,
  version: ScriptApiVersion
): Promise<PhoneScript | null> {
  const body =
    version === 2
      ? await sanitizePhoneScriptForSave(script, {
          isV2: true,
          modifiedUseCases: new Map(
            (script.useCases ?? []).map((useCase) => [useCase.id, useCase])
          ),
        })
      : script;

  return apiFetch<PhoneScript>(
    getScriptApiUrl(script.id, ScriptType.Phone, version),
    "PUT",
    { body }
  );
}

/** Hydrate from owned `useCases[]` on v2; otherwise fetch the use-case library. */
export async function loadAssociatedUseCases(
  script: PhoneScript,
  version: ScriptApiVersion
): Promise<UseCase[]> {
  if (shouldSkipUseCaseBatchFetch(version === 2, script)) {
    return hydrateUseCasesFromScript(script);
  }

  const useCaseIds = script.useCaseAssociations?.map((association) => association.id) ?? [];
  if (useCaseIds.length === 0) {
    return [];
  }

  const fetched = await Promise.all(
    useCaseIds.map((id) => apiLive<UseCase>(`${GAIL_API}/v1/use_cases/${id}`))
  );
  return fetched.filter((useCase): useCase is UseCase => Boolean(useCase));
}
